import { getStringValue } from '~/modules/guarantees/utils.ts';
import type { Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { errorMessages } from '~/constants/errorMessages.ts';
import { formatDateForDisplay, isValidDateFormat, parseDate } from '~/helpers/utils.ts';
import {
  GuaranteesFileType,
  type TGuaranteesApplication,
  type TGuaranteesApplicationFile,
  type TGuaranteesIssueGuaranteeDetails,
  type TPatchGuaranteesIssueGuaranteePayload,
} from '~/store/guarantees/types.ts';
import { UDBTF_GUARANTEE_SCAN_COPY_TEXT } from './constants.ts';
import {
  GuaranteesIssueGuaranteeFieldName,
  type TGuaranteesIssueGuaranteeFormValues,
} from './types.ts';

export const mapUdbtfGuaranteeScanCopyFile = (
  files: TGuaranteesApplicationFile[] = []
) => {
  const scanCopyFile = files.find(
    (file) => file.fileType === GuaranteesFileType.GUARANTEE_SCAN_COPY
  );

  if (!scanCopyFile) {
    return null;
  }

  const applicationFileId = getStringValue(scanCopyFile.id);
  const fileId = getStringValue(scanCopyFile.fileId);

  if (!applicationFileId && !fileId) {
    return null;
  }

  return {
    id: applicationFileId || fileId,
    applicationFileId,
    fileId,
    fileName:
      getStringValue(scanCopyFile.description) || UDBTF_GUARANTEE_SCAN_COPY_TEXT.fallbackFileName,
  };
};

const UDBTF_OPERATION_FINISHED_BEFORE_PROVIDED_MESSAGE =
  'Дата завершення операції не може бути раніше дати надання гарантії';

const getIssueDetailsValue = (
  application: TGuaranteesApplication | null | undefined,
  key: keyof TGuaranteesIssueGuaranteeDetails
): string => {
  const managementAccountingValue =
    key === 'managementAccountingProduct'
      ? application?.managementAccounting?.product
      : application?.managementAccounting?.[key];
  const issueDetailsValue = application?.issueDetails?.[key];
  const applicationValue = application?.[key as keyof TGuaranteesApplication];

  return getStringValue(managementAccountingValue ?? issueDetailsValue ?? applicationValue);
};

const formatDateToBackendDate = (value: string): string => {
  if (!isValidDateFormat(value)) {
    return '';
  }

  const [day, month, year] = value.split('.');

  return `${year}-${month}-${day}`;
};

export const getGuaranteesIssueGuaranteeInitialValues = (
  application: TGuaranteesApplication | null | undefined
): TGuaranteesIssueGuaranteeFormValues => ({
  [GuaranteesIssueGuaranteeFieldName.ManagementAccountingProduct]: getIssueDetailsValue(
    application,
    'managementAccountingProduct'
  ),
  [GuaranteesIssueGuaranteeFieldName.OperationProvidedDate]: formatDateForDisplay(
    getIssueDetailsValue(application, 'operationProvidedDate')
  ),
  [GuaranteesIssueGuaranteeFieldName.OperationFinishedDate]: formatDateForDisplay(
    getIssueDetailsValue(application, 'operationFinishedDate')
  ),
});

export const guaranteesIssueGuaranteeValidationSchema = yup.object({
  [GuaranteesIssueGuaranteeFieldName.ManagementAccountingProduct]: yup.string(),
  [GuaranteesIssueGuaranteeFieldName.OperationProvidedDate]: yup
    .string()
    .required(errorMessages.FIELD_REQUIRED)
    .test('operation-provided-date-format', errorMessages.CORRECT_DATE_FORMAT, (value) =>
      isValidDateFormat(getStringValue(value))
    ),
  [GuaranteesIssueGuaranteeFieldName.OperationFinishedDate]: yup
    .string()
    .test('operation-finished-date-format', errorMessages.CORRECT_DATE_FORMAT, (value) => {
      const normalizedValue = getStringValue(value);

      return !normalizedValue || isValidDateFormat(normalizedValue);
    })
    .test(
      'operation-finished-date-after-provided-date',
      UDBTF_OPERATION_FINISHED_BEFORE_PROVIDED_MESSAGE,
      (value, context) => {
        const finishedDate = getStringValue(value);
        const providedDate = getStringValue(
          context.parent?.[GuaranteesIssueGuaranteeFieldName.OperationProvidedDate]
        );

        if (!finishedDate || !isValidDateFormat(finishedDate) || !isValidDateFormat(providedDate)) {
          return true;
        }

        const parsedFinishedDate = parseDate(finishedDate);
        const parsedProvidedDate = parseDate(providedDate);

        if (!parsedFinishedDate || !parsedProvidedDate) {
          return true;
        }

        return parsedFinishedDate.getTime() >= parsedProvidedDate.getTime();
      }
    ),
});

export const guaranteesIssueGuaranteeResolver: Resolver<
  TGuaranteesIssueGuaranteeFormValues
> = yupResolver(guaranteesIssueGuaranteeValidationSchema) as unknown as Resolver<
  TGuaranteesIssueGuaranteeFormValues
>;

export const buildGuaranteesIssueGuaranteePayload = (
  values: TGuaranteesIssueGuaranteeFormValues
): TPatchGuaranteesIssueGuaranteePayload => ({
  operationProvidedDate: formatDateToBackendDate(
    values[GuaranteesIssueGuaranteeFieldName.OperationProvidedDate]
  ),
  operationFinishedDate: getStringValue(
    values[GuaranteesIssueGuaranteeFieldName.OperationFinishedDate]
  )
    ? formatDateToBackendDate(values[GuaranteesIssueGuaranteeFieldName.OperationFinishedDate])
    : null,
});
